/**
 * Playbooks de aterrizaje — retención / capturas / Pro por perfil y etapa.
 * RPC: get_early_landing_playbooks(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { clampLandingAnalyticsDays } from '@/components/analytics/periodOptions';

export type PlaybookGoal = 'retention' | 'captures' | 'pro';

export interface PlaybookDriver {
    key: string;
    label: string;
    did_n: number;
    skip_n: number;
    rate_did: number | null;
    rate_skip: number | null;
    lift: number | null;
    d7_did?: number | null;
    d7_skip?: number | null;
    lift_d7?: number | null;
}

export interface PlaybookBestAction {
    key: string;
    label: string;
    did_n: number;
    retained_did: number | null;
    retained_skip: number | null;
    lift: number | null;
}

export interface PlaybookProfile {
    profile: string;
    users: number;
    session_5_pct: number | null;
    day_7_pct: number | null;
    creator_pct: number | null;
    pro_pct: number | null;
    rincon_48h_pct: number | null;
    intel_48h_pct: number | null;
    follow_48h_pct: number | null;
    lurker_pct: number | null;
    best_retention_action: PlaybookBestAction | null;
}

export interface PlaybookStage {
    stage: string;
    label: string;
    eligible: number;
    active: number;
    active_pct: number | null;
    creators_in_stage: number;
    session_5_among_active: number | null;
}

export interface LandingPlaybook {
    id: string;
    goal: PlaybookGoal | string;
    priority: number;
    title: string;
    why: string;
    evidence: string;
    audience: string;
    stage: string;
    action_key: string | null;
    do_now: string[];
    success_metric: string;
}

export interface EarlyLandingPlaybooks {
    period_days: number;
    methodology: string | Record<string, string>;
    cohort: {
        registered: number;
        creators: number;
        creator_pct: number;
        pros: number;
        pro_pct: number;
        retained_session_5: number;
        retained_pct: number;
        missing_experience_level: number;
    };
    drivers: {
        retention: PlaybookDriver[];
        captures: PlaybookDriver[];
        pro: PlaybookDriver[];
    };
    by_profile: PlaybookProfile[];
    by_stage: PlaybookStage[];
    playbooks: LandingPlaybook[];
    executive_summary: LandingPlaybook[];
}

export const PLAYBOOK_GOAL_LABELS: Record<string, string> = {
    retention: 'Retención',
    captures: 'Capturas',
    pro: 'Pro',
};

export const STAGE_ORDER = ['d0_d1', 'd2_d3', 'd4_d7', 'd8_d14', 'd15_d30'] as const;

function num(v: unknown): number {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function numOrNull(v: unknown): number | null {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function asDriver(raw: unknown): PlaybookDriver | null {
    if (!raw || typeof raw !== 'object') return null;
    const d = raw as Record<string, unknown>;
    if (!d.key) return null;
    return {
        key: String(d.key),
        label: String(d.label ?? d.key),
        did_n: num(d.did_n ?? d.did_users),
        skip_n: num(d.skip_n ?? d.skip_users),
        rate_did: numOrNull(d.rate_did),
        rate_skip: numOrNull(d.rate_skip),
        lift: numOrNull(d.lift),
        d7_did: numOrNull(d.d7_did),
        d7_skip: numOrNull(d.d7_skip),
        lift_d7: numOrNull(d.lift_d7 ?? d.secondary_lift_d7),
    };
}

function asPlaybook(raw: unknown): LandingPlaybook | null {
    if (!raw || typeof raw !== 'object') return null;
    const p = raw as Record<string, unknown>;
    if (!p.id || !p.title) return null;
    const doNow = Array.isArray(p.do_now)
        ? p.do_now.map((x) => String(x)).filter(Boolean)
        : [];
    return {
        id: String(p.id),
        goal: String(p.goal ?? 'retention'),
        priority: num(p.priority),
        title: String(p.title),
        why: String(p.why ?? ''),
        evidence: String(p.evidence ?? ''),
        audience: String(p.audience ?? ''),
        stage: String(p.stage ?? ''),
        action_key: p.action_key == null ? null : String(p.action_key),
        do_now: doNow,
        success_metric: String(p.success_metric ?? ''),
    };
}

function asProfile(raw: unknown): PlaybookProfile | null {
    if (!raw || typeof raw !== 'object') return null;
    const p = raw as Record<string, unknown>;
    if (!p.profile) return null;
    let best: PlaybookBestAction | null = null;
    if (p.best_retention_action && typeof p.best_retention_action === 'object') {
        const b = p.best_retention_action as Record<string, unknown>;
        if (b.key) {
            best = {
                key: String(b.key),
                label: String(b.label ?? b.key),
                did_n: num(b.did_n ?? b.did_users),
                retained_did: numOrNull(b.retained_did ?? b.rate_did),
                retained_skip: numOrNull(b.retained_skip ?? b.rate_skip),
                lift: numOrNull(b.lift),
            };
        }
    }
    return {
        profile: String(p.profile),
        users: num(p.users),
        session_5_pct: numOrNull(p.session_5_pct),
        day_7_pct: numOrNull(p.day_7_pct),
        creator_pct: numOrNull(p.creator_pct),
        pro_pct: numOrNull(p.pro_pct),
        rincon_48h_pct: numOrNull(p.rincon_48h_pct),
        intel_48h_pct: numOrNull(p.intel_48h_pct),
        follow_48h_pct: numOrNull(p.follow_48h_pct),
        lurker_pct: numOrNull(p.lurker_pct),
        best_retention_action: best,
    };
}

function asStage(raw: unknown): PlaybookStage | null {
    if (!raw || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    if (!s.stage) return null;
    return {
        stage: String(s.stage),
        label: String(s.label ?? s.stage),
        eligible: num(s.eligible),
        active: num(s.active),
        active_pct: numOrNull(s.active_pct),
        creators_in_stage: num(s.creators_in_stage),
        session_5_among_active: numOrNull(s.session_5_among_active),
    };
}

export function parseEarlyLandingPlaybooks(raw: unknown): EarlyLandingPlaybooks | null {
    if (raw == null) return null;

    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];

    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        const wrapped = obj.get_early_landing_playbooks;
        if (wrapped && typeof wrapped === 'object') payload = wrapped;
    }

    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    if (typeof p.period_days !== 'number') return null;

    const cohortRaw = (p.cohort && typeof p.cohort === 'object' ? p.cohort : {}) as Record<string, unknown>;
    const driversRaw = (p.drivers && typeof p.drivers === 'object' ? p.drivers : {}) as Record<string, unknown>;

    const retention = Array.isArray(driversRaw.retention)
        ? driversRaw.retention.map(asDriver).filter((x): x is PlaybookDriver => !!x)
        : [];
    const captures = Array.isArray(driversRaw.captures)
        ? driversRaw.captures.map(asDriver).filter((x): x is PlaybookDriver => !!x)
        : [];
    const pro = Array.isArray(driversRaw.pro)
        ? driversRaw.pro.map(asDriver).filter((x): x is PlaybookDriver => !!x)
        : [];

    return {
        period_days: p.period_days,
        methodology: (typeof p.methodology === 'string' || (p.methodology && typeof p.methodology === 'object'))
            ? (p.methodology as string | Record<string, string>)
            : '',
        cohort: {
            registered: num(cohortRaw.registered),
            creators: num(cohortRaw.creators),
            creator_pct: num(cohortRaw.creator_pct),
            pros: num(cohortRaw.pros),
            pro_pct: num(cohortRaw.pro_pct),
            retained_session_5: num(cohortRaw.retained_session_5),
            retained_pct: num(cohortRaw.retained_pct),
            missing_experience_level: num(cohortRaw.missing_experience_level),
        },
        drivers: { retention, captures, pro },
        by_profile: Array.isArray(p.by_profile)
            ? p.by_profile.map(asProfile).filter((x): x is PlaybookProfile => !!x)
            : [],
        by_stage: Array.isArray(p.by_stage)
            ? p.by_stage.map(asStage).filter((x): x is PlaybookStage => !!x)
            : [],
        playbooks: Array.isArray(p.playbooks)
            ? p.playbooks.map(asPlaybook).filter((x): x is LandingPlaybook => !!x)
            : [],
        executive_summary: Array.isArray(p.executive_summary)
            ? p.executive_summary.map(asPlaybook).filter((x): x is LandingPlaybook => !!x)
            : [],
    };
}

export function useEarlyLandingPlaybooks(days: number = 7, enabled = true) {
    const safeDays = clampLandingAnalyticsDays(days);

    return useQuery<EarlyLandingPlaybooks>({
        queryKey: ['admin', 'early-landing-playbooks', safeDays],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_early_landing_playbooks', {
                p_days: safeDays,
            });
            if (error) throw error;
            const parsed = parseEarlyLandingPlaybooks(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_early_landing_playbooks');
            }
            return parsed;
        },
        enabled,
        retry: 0,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}
